import { useAuth } from '@platform/auth-client';
import type { Column } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { acceptsParcelChanges, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { ORDERS_DISPATCH_ROLE, type Order } from './orders.ts';
import { OrderRowActions } from './OrderRowActions.tsx';
import { useAvailableOrderActions } from './useAvailableOrderActions.ts';
import type { OrderMoves } from './useOrderMoves.ts';
import { useShipmentLabel } from './useShipmentLabel.ts';

export const useOrderActionsColumn = (
  shipping: ShippingClient,
  moves: OrderMoves,
  onOpen: (order: Order) => void,
): Column<Order> => {
  const { t, warn, describeError } = usePageContext();
  const carriers = useCarriers(shipping);
  const availableFor = useAvailableOrderActions(acceptsParcelChanges(carriers.data?.items));
  const mayPrintLabel = useAuth().hasRole(ORDERS_DISPATCH_ROLE);
  const label = useShipmentLabel(shipping);
  const printLabel = (shipmentId: string) => {
    label.mutate(
      { shipmentId, use: 'print' },
      {
        onError: (error) => {
          warn(describeError(error) ?? t('orders.labelPrintFailed'));
        },
      },
    );
  };
  return {
    id: 'actions',
    header: t('catalog.actions'),
    action: true,
    cell: (order) => (
      <OrderRowActions
        order={order}
        available={availableFor(order)}
        busy={moves.actions.isPending || label.isPending}
        mayPrintLabel={mayPrintLabel}
        onOpen={onOpen}
        onAction={moves.run}
        onPrintLabel={printLabel}
      />
    ),
  };
};
